import { Alert } from '@/components/DynamicUI/Alert';
import { AlertSeverity } from '@/constants/alertTypes';
import { Card, Icon, styled, Typography, Skeleton, Box } from '@mui/material';
import NumberFlow from '@number-flow/react';
import { ModalLink } from './formElements/typographies/ModalLink';
import { LinkTypes } from '@/constants/linkTypes';
import {
  TypographyAlign,
  TypographyColors,
  TypographyFontWeight,
  TypographyVariants,
} from '@/constants/typographyTypes';
import { PartnerCommitmentPriceCard as PartnerCommitmentPriceCardType } from '@/schemas/sceneDataSchema';
import { PricingTerm } from '@/constants/pricingTypes';
import { hasValidationErrors, useQuote, useShowLoader } from '@/hooks/use-quote';
import { ButtonSizes, ButtonTypes, ButtonColors, ButtonVariants } from '@/constants/buttonTypes';
import { DynamicRenderer } from './DynamicRenderer';
import { ModalDialog } from './ModalDialog';
import { CoverageSubmitButton } from './formElements/buttons/CoverageSubmitButton';

// Smart Component - requires zustand quote cache
export const PartnerCommitmentPriceCard = ({ component }: { component: PartnerCommitmentPriceCardType }) => {
  const quote = useQuote();
  const showLoader = useShowLoader();
  const hasErrors = quote ? hasValidationErrors(quote) : false;

  if (!quote) {
    return (
      <StyledCard variant='outlined'>
        <Typography variant='body1'>Loading pricing...</Typography>
      </StyledCard>
    );
  }

  const { initial_term_selection, driver_count, vehicle_count, policyholder_first_name, estimated_quote } =
    quote.details;

  const headingText = policyholder_first_name
    ? `Hey ${policyholder_first_name}, review your 6-month coverage plan.`
    : 'Review your 6-month coverage plan.';

  const dueToday = 0;

  const driverText = `${driver_count} driver${driver_count > 1 ? 's' : ''}`;
  const vehicleText = `${vehicle_count} vehicle${vehicle_count > 1 ? 's' : ''}`;

  return (
    <>
      <Typography variant='h2'>{headingText}</Typography>
      <StyledCard variant='outlined'>
        <DriverVehicleModal>
          {component.profile_modal_id && (
            <>
              <IdCard color='primary'>id_card</IdCard>
              <ModalLink
                component={{
                  type: LinkTypes.MODAL,
                  text: `${driverText}, ${vehicleText}`,
                  method_type: 'open',
                  modal_id: component.profile_modal_id,
                  color: TypographyColors.PRIMARY,
                  align: TypographyAlign.LEFT,
                  variant: TypographyVariants.BODY1,
                  font_weight: TypographyFontWeight.MEDIUM,
                }}
              />
            </>
          )}
        </DriverVehicleModal>

        <Typography variant={TypographyVariants.H1}>
          <NumberFlow
            value={0}
            format={{ style: 'currency', currency: 'USD' }}
            suffix={initial_term_selection === PricingTerm.MONTHLY ? '/mo' : '/6 mo'}
            spinTiming={{ duration: 750, easing: 'ease-in-out' }}
            opacityTiming={{ duration: 350, easing: 'ease-in-out' }}
            willChange
          />
        </Typography>

        {component.price_estimate_modal_id && estimated_quote && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ModalLink
              component={{
                type: LinkTypes.MODAL,
                text: 'Price estimate',
                method_type: 'open',
                modal_id: component.price_estimate_modal_id,
                color: TypographyColors.TEXT_SECONDARY,
                align: TypographyAlign.LEFT,
                variant: TypographyVariants.BODY1,
                font_weight: TypographyFontWeight.REGULAR,
              }}
            />
            <Icon color='secondary'>help_outline</Icon>
          </Box>
        )}

        <DueTodayRow>
          {dueToday !== null && dueToday !== undefined ? (
            <>
              {showLoader ? (
                <Skeleton
                  variant='rounded'
                  width='100%'
                  height={27}
                  animation='wave'
                />
              ) : (
                <>
                  <Typography variant='h4'>
                    <NumberFlow
                      value={dueToday ?? 0}
                      format={{ style: 'currency', currency: 'USD' }}
                      suffix=' Due today'
                      spinTiming={{ duration: 750, easing: 'ease-in-out' }}
                      opacityTiming={{ duration: 350, easing: 'ease-in-out' }}
                      willChange
                    />
                  </Typography>
                  {component.payment_details_modal_id && (
                    <ModalLink
                      component={{
                        type: LinkTypes.MODAL,
                        text: 'See details',
                        method_type: 'open',
                        modal_id: component.payment_details_modal_id,
                        color: TypographyColors.TEXT_PRIMARY,
                        align: TypographyAlign.LEFT,
                        variant: TypographyVariants.BODY2,
                        font_weight: TypographyFontWeight.REGULAR,
                      }}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <Typography variant='h4'>Amount due today calculated at checkout</Typography>
          )}
        </DueTodayRow>
      </StyledCard>
      {hasErrors && (
        <>
          <ShowOnMobile>
            <StyledFixedAlert>
              <Alert
                severity={AlertSeverity.ERROR}
                title={null}
                titleGutterBottom={false}
                text={'There is a conflict with your selections. Please resolve before continuing.'}
              />
            </StyledFixedAlert>
          </ShowOnMobile>
          <ShowOnDesktop>
            <Alert
              severity={AlertSeverity.ERROR}
              title={'Invalid Coverage Selection'}
              titleGutterBottom={false}
              text={'There is a conflict with your selections. Please resolve before continuing.'}
            />
          </ShowOnDesktop>
        </>
      )}
      <ShowOnDesktop>
        <CoverageSubmitButton
          component={{
            id: component.form_id,
            form_id: component.form_id,
            label: 'Continue to checkout',
            size: ButtonSizes.LARGE,
            color: ButtonColors.PRIMARY,
            start_icon: null,
            variant: ButtonVariants.CONTAINED,
            type: ButtonTypes.COVERAGE_SUBMIT,
          }}
        />
      </ShowOnDesktop>
      <ShowOnMobile>
        <ScrollNudge>
          <Typography
            variant='body2'
            align='center'
            fontWeight={'medium'}
          >
            Scroll to customize your plan
          </Typography>
          <Icon fontSize='small'>arrow_downward</Icon>
        </ScrollNudge>
      </ShowOnMobile>
      <ModalDialog
        children={
          <DynamicRenderer
            components={quote.payment_modal_content}
            route={async () => {}}
          />
        }
        id={component.payment_details_modal_id}
        status='closed'
      />
    </>
  );
};

const ShowOnDesktop = styled('div')`
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
`;

const ShowOnMobile = styled('div')`
  @media (min-width: 768px) {
    display: none;
  }
`;

const bounceAnimation = `
  @keyframes bounce {
    0%, 20%, 100% {
      transform: translateY(0);
    }
    10% {
      transform: translateY(5px);
    }
  }
`;

const ScrollNudge = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: -8px;

  ${bounceAnimation}

  & > .MuiIcon-root {
    animation: bounce 3.5s ease-in-out infinite;
  }
`;

const StyledCard = styled(Card)`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 8px;
  border-radius: var(--border-radius-medium);
`;

const DriverVehicleModal = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: var(--3, 8px);
  align-self: stretch;
`;

const IdCard = styled(Icon)`
  flex-shrink: 0;
  height: 24px;
  width: 24px;
  font-family: 'Material Symbols Outlined';
`;

const DueTodayRow = styled('div')`
  display: flex;
  align-items: center;
  gap: var(--3, 8px);
  align-self: stretch;
`;

const StyledFixedAlert = styled('div')`
  position: fixed;
  inset: auto 10px 80px;
  z-index: 1100;
`;
